import React, { useEffect, useMemo, useState } from 'react';
import DocFaby from '../../images/foto-faby.478b28e0.jpg';
import DocMontse from '../../images/foto-monse.64d09495.jpg';
import '../../css/About.css';
import TituloPagina from '../../js/Components/PageTitle';

type Dentist = {
  id: number | string;
  name: string;
  role: string;
  photo: string;
  phone: string;
  email: string;
  address: string;
  accent: string;
};

type ApiDentist = {
  id?: number;
  nombre?: string;
  apellidos?: string;
  telefono?: string;
  email?: string;
  direccion?: string;
  titulo?: string;
  titulo2?: string;
  foto?: string;
};

type DentistsResponse = {
  ok?: boolean;
  items?: ApiDentist[];
};

const FALLBACK_DENTISTS: Dentist[] = [
  {
    id: 'fallback-faby',
    name: 'Dra. Ana Fabiola Puente Villafaña',
    role: 'Cirujano Dentista y Especialista en Ortodoncia',
    photo: DocFaby,
    phone: '33 34 75 81 91',
    email: 'faby.puente@live.com',
    address: 'Teodomiro Manzano 4365, Guadalajara, Jal.',
    accent: 'Ortodoncia y atención integral',
  },
  {
    id: 'fallback-montse',
    name: 'Dra. Montserrat Puente Villafaña',
    role: 'Cirujano Dentista y Especialista en Endodoncia',
    photo: DocMontse,
    phone: '3316369641',
    email: 'montc.mcr@gmail.com',
    address: 'Teodomiro Manzano 4365, Guadalajara, Jal.',
    accent: 'Endodoncia y atención integral',
  },
];

const API_URL = process.env.REACT_APP_ODONTOLOGOS_API || '/admin/api/odontologos_public.php';

const normalizeDentist = (item: ApiDentist): Dentist => {
  const fullName = `${item.nombre || ''} ${item.apellidos || ''}`.trim();
  return {
    id: item.id || fullName || Math.random().toString(36),
    name: fullName || 'Odontólogo DentalPuente',
    role: item.titulo2 || 'Cirujano dentista',
    photo: item.foto || '',
    phone: item.telefono || '',
    email: item.email || '',
    address: item.direccion || 'Teodomiro Manzano 4365, Guadalajara, Jal.',
    accent: item.titulo || 'Atención dental integral',
  };
};

const About: React.FC = () => {
  const [dbDentists, setDbDentists] = useState<Dentist[]>([]);
  const [isLoadingDentists, setIsLoadingDentists] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    fetch(API_URL, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error('No se pudo leer odontólogos');
        }
        return response.json() as Promise<DentistsResponse>;
      })
      .then((data) => {
        if (!isMounted) return;
        const items = Array.isArray(data.items) ? data.items.map(normalizeDentist) : [];
        setDbDentists(items.filter((item) => item.name.trim() !== ''));
      })
      .catch(() => {
        if (isMounted) {
          setDbDentists([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingDentists(false);
        }
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const dentists = useMemo(() => (dbDentists.length > 0 ? dbDentists : FALLBACK_DENTISTS), [dbDentists]);

  return (
    <div>
      <TituloPagina title="DentalPuente | Acerca De" pageSlug="about" />

      <main className="about-page content-wrap">
        <section className="about-hero">
          <div>
            <span className="dp-eyebrow">Acerca de DentalPuente</span>
            <h1>Un equipo dental cercano, profesional y claro contigo</h1>
            <p>
              En DentalPuente buscamos que cada paciente se sienta escuchado, bien orientado y acompañado durante su tratamiento dental.
            </p>
          </div>
          <div className="about-hero-card">
            <strong>Teodomiro Manzano 4365</strong>
            <span>Guadalajara, Jalisco</span>
            <a href="/Contact">Ver contacto</a>
          </div>
        </section>

        <section className="about-values-grid">
          <article>
            <span>01</span>
            <h3>Diagnóstico honesto</h3>
            <p>Explicamos tu caso y las opciones disponibles sin complicarte.</p>
          </article>
          <article>
            <span>02</span>
            <h3>Tratamiento humano</h3>
            <p>Atención amable para que tu visita sea más cómoda y tranquila.</p>
          </article>
          <article>
            <span>03</span>
            <h3>Seguimiento dental</h3>
            <p>Nos enfocamos en cuidar el resultado y prevenir problemas futuros.</p>
          </article>
        </section>

        <section className="about-team-section">
          <div className="about-section-header">
            <span className="dp-eyebrow">Nuestro equipo</span>
            <h2>Doctoras DentalPuente</h2>
            <p>Profesionales enfocadas en salud, estética, prevención y bienestar dental.</p>
            {isLoadingDentists && <small className="about-loading-note">Actualizando perfiles desde DentalPuente...</small>}
          </div>

          <div className="about-dentist-list">
            {dentists.map((dentist) => (
              <article className="about-dentist-card" key={dentist.id}>
                <div className="about-dentist-photo-wrap">
                  {dentist.photo ? (
                    <img src={dentist.photo} alt={dentist.name} className="about-dentist-photo" />
                  ) : (
                    <div className="about-dentist-photo about-dentist-photo-empty">DP</div>
                  )}
                </div>
                <div className="about-dentist-info">
                  <span className="about-specialty-pill">{dentist.accent}</span>
                  <h3>{dentist.name}</h3>
                  <h4>{dentist.role}</h4>
                  <div className="about-contact-grid">
                    <div className="about-contact-item">
                      <span>☎</span>
                      <div>
                        <strong>Teléfono</strong>
                        <p>{dentist.phone || 'Disponible en recepción'}</p>
                      </div>
                    </div>
                    <div className="about-contact-item">
                      <span>✉</span>
                      <div>
                        <strong>Email</strong>
                        <p>{dentist.email || 'Disponible en recepción'}</p>
                      </div>
                    </div>
                    <div className="about-contact-item about-contact-wide">
                      <span>⌖</span>
                      <div>
                        <strong>Dirección</strong>
                        <p>{dentist.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default About;
